import * as React from 'react';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Panel from '../panel/Panel';
import { useStores } from '../../stores';
import { observer } from 'mobx-react-lite';
import { useTranslation } from "react-i18next";
import { ScheduleWeek } from '../../domain/ScheduleWeek';
import Can from '../../components/Can';
import ScheduleWeekCreateDialog from './dialogs/ScheduleWeekCreateDialog';
import ScheduleWeekDialog from './dialogs/ScheduleWeekDialog';
import { useOpenDialog } from '../../hooks/useOpenDialog';
import ScheduleContent from './ScheduleContent';
import ScheduleEpubImportDialog from './dialogs/ScheduleEpubImportDialog';
import ScheduleDownloadExcelDialog from './dialogs/ScheduleDownloadExcelDialog';
import ScheduleEntryCreateDialog from './dialogs/ScheduleEntryCreateDialog';
import { ScheduleEntry } from '../../domain/ScheduleEntry';
import { isMobile } from 'react-device-detect';
import { useConfirm } from 'material-ui-confirm';
import LoadingDimmer from '../../components/LoadingDimmer';
import ScheduleEntryDialog from './dialogs/ScheduleEntryDialog';
import ScheduleAutoArrangementButton from './ScheduleAutoArrangementButton';
import SchedulePublisherHistoryDialog from './dialogs/SchedulePublisherHistoryDialog';
import ScheduleAssignmentHistoryDialog from './dialogs/ScheduleAssignmentHistoryDialog';
import SchedulePageMenu from './menu/SchedulePageMenu';
import ScheduleCreateFieldServiceEntryDialog from './dialogs/ScheduleCreateFieldServiceEntryDialog';

const SchedulePage = observer(() => {
    const { t } = useTranslation();
    const { scheduleStore, appointmentStore } = useStores();
    const confirm = useConfirm();
    const [generateLoading, setGenerateLoading] = React.useState<boolean>(false);
    const [selectedEntry, setSelectedEntry] = React.useState<ScheduleEntry | undefined>();
    const { isOpen, openDialog, closeDialog } = useOpenDialog();

    const handleEntryCreate = (entry: ScheduleEntry) => {
        scheduleStore.createEntry({ ...entry, weekId: scheduleStore.currentWeek!.id! });
    }

    const saveWeekUpdate = (week: ScheduleWeek) => {
        const { entries, ...rest } = week;
        scheduleStore.updateWeek(rest as ScheduleWeek);
    }

    const handleWeekCreate = (week: ScheduleWeek) => {
        scheduleStore.createWeek(week);
    }

    const handleWeekDelete = () => {
        const currentWeek = scheduleStore.currentWeek;
        if (!currentWeek?.id) {
            return;
        }

        const targetName = currentWeek.name ? ` ${currentWeek.name}` : ` ${t("week")}`;
        confirm({
            title: t("are_you_sure"),
            description: `${t("this_will_permanently_delete")} ${targetName}`,
            confirmationText: t("yes"),
            cancellationText: t("no")
        }).then(() => {
            scheduleStore.deleteWeek(currentWeek.id!, scheduleStore.currentWeekDate);
        });
    }

    const saveUpdatedEntry = (entry: ScheduleEntry) => {
        scheduleStore.updateEntry(entry);
    }

    const handleEntryEdit = (entry: ScheduleEntry) => {
        setSelectedEntry(entry);
        openDialog("entry-dialog")
    }

    const handlePublisherScheduleHistory = (entry: ScheduleEntry) => {
        setSelectedEntry(entry);
        openDialog("publisher-history-dialog")
    }

    const handleEntryAssignmentHistory = (entry: ScheduleEntry) => {
        setSelectedEntry(entry);
        openDialog("assignment-history-dialog");
    }

    const handleEntryDownload = (entry: ScheduleEntry) => {
        scheduleStore.downloadEntryS89(entry);
    }

    const handleCloseDialog = () => {
        setSelectedEntry(undefined);
        closeDialog();
    }

    const handleEntryDelete = (entry: ScheduleEntry) => {
        confirm({
            title: t("are_you_sure"),
            description: `${t("this_will_permanently_delete")}`,
            confirmationText: t("yes"),
            cancellationText: t("no")
        }).then(() => {
            scheduleStore.deleteEntry(entry);
        });
    }

    const handleAutoArrangement = (type: string) => {
        setGenerateLoading(true);
        if (scheduleStore.currentWeek) {
            scheduleStore.generateWeek(scheduleStore.currentWeek!.id!, type, () => {
                setGenerateLoading(false);
            });
        }
    }

    return (
        <Panel
            title={t("schedule")}
            headerItems={SchedulePageMenu({
                onExcelDownload: () => openDialog("excel-dialog"),
                onEpubUpload: () => openDialog("epub-dialog"),
                onScheduleWeekCreate: () => openDialog("create-week-dialog"),
                onScheduleWeekEdit: () => openDialog("week-dialog"),
                onScheduleWeekDelete: handleWeekDelete,
                onCreateScheduleEntry: () => openDialog("create-entry-dialog"),
                onCreateFieldServiceEntry: () => openDialog("create-field-service-entry-dialog"),
                onCreateSpeechEntry: () => openDialog("create-entry-dialog")
            })}>
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {!isMobile && (
                        <Can do="manage" on='ScheduleEntry'>
                            <div style={{ marginLeft: "auto", marginRight: 10 }} >
                                <ScheduleAutoArrangementButton
                                    onAutoArrangementSelect={handleAutoArrangement}
                                />
                            </div>
                        </Can>
                    )}

                    <ScheduleContent
                        onEntryUpdate={saveUpdatedEntry}
                        onEntryEdit={handleEntryEdit}
                        onPublisherScheduleHistory={handlePublisherScheduleHistory}
                        onEntryAssignmentHistory={handleEntryAssignmentHistory}
                        onEntryDownload={handleEntryDownload}
                    />
                </Paper>
            </Container>

            <ScheduleEpubImportDialog
                isOpen={isOpen("epub-dialog")}
                onClose={handleCloseDialog}
            />
            <ScheduleDownloadExcelDialog
                isOpen={isOpen("excel-dialog")}
                onClose={handleCloseDialog}
            />
            <ScheduleEntryCreateDialog
                isOpen={isOpen("create-entry-dialog")}
                onSubmit={handleEntryCreate}
                onClose={handleCloseDialog}
            />
            <ScheduleCreateFieldServiceEntryDialog
                isOpen={isOpen("create-field-service-entry-dialog")}
                onSubmit={handleEntryCreate}
                onClose={handleCloseDialog}
            />
            <ScheduleWeekCreateDialog
                isOpen={isOpen("create-week-dialog")}
                weekDate={scheduleStore.currentWeekDate}
                onSubmit={handleWeekCreate}
                onClose={handleCloseDialog}
            />
            {scheduleStore.currentWeek && <ScheduleWeekDialog
                week={scheduleStore.currentWeek}
                isOpen={isOpen("week-dialog")}
                onSubmit={saveWeekUpdate}
                onClose={handleCloseDialog}
            />}
            {selectedEntry && <ScheduleEntryDialog
                entry={selectedEntry}
                assignments={appointmentStore.assignments}
                isOpen={isOpen("entry-dialog")}
                onSubmit={saveUpdatedEntry}
                onDelete={handleEntryDelete}
                onClose={handleCloseDialog}
            />}

            {selectedEntry && <SchedulePublisherHistoryDialog
                entry={selectedEntry}
                isOpen={isOpen("publisher-history-dialog")}
                onClose={handleCloseDialog}
            />}

            {selectedEntry && <ScheduleAssignmentHistoryDialog
                entry={selectedEntry}
                isOpen={isOpen("assignment-history-dialog")}
                onClose={handleCloseDialog}
            />}

            <LoadingDimmer enabled={generateLoading} />
        </Panel >
    );
});

export default SchedulePage;